import { MAINTAINER_PATHS } from '@liquid-spirit/api/domains/maintainer';
import { getWebVitalSnapshotKey, sameMetricSnapshot, sanitizeMetricRoute } from '@liquid-spirit/observability';
import apiClient from './apiClient';
import { resolveStoredAuthToken } from '../utils/storage';
import { dataFlowLog } from '../utils/dataFlowLogger';

const STORAGE_KEY = 'ls:maintainer-metrics:v1';
const UPDATE_EVENT = 'ls:maintainer-metrics:update';
const MAX_SAMPLES = 800;
const MAX_PENDING = 160;
const SAMPLE_RETENTION_MS = 14 * 24 * 60 * 60 * 1000;
const DUPLICATE_REQUEST_WINDOW_MS = 2500;
const ROUTE_JS_FLUSH_DELAY_MS = 1400;
const DEFAULT_RUM_ENDPOINT = MAINTAINER_PATHS.rumMetrics;
const DEFAULT_SUMMARY_ENDPOINT = MAINTAINER_PATHS.summaryMetrics;
const APPLICATION = 'liquid-spirit-web';
const PLATFORM = 'web';
const ROUTE_JOURNEYS = new Set([
  'homeFeed',
  'profile',
  'postDetail',
  'maintainerDashboard',
  'maintainerCommunities',
  'maintainerCommunityDetail',
  'maintainerMembers',
  'maintainerBodies',
  'maintainerImports',
  'maintainerRoles',
  'maintainerPermissions',
]);
const CACHE_HIT_STATUSES = new Set(['memory-hit', 'persisted-hit', 'stale-hit', '304', 'deduped']);
const reportMetricsFlushError = (error, source) => {
  dataFlowLog('maintainer-metrics.flush.error', {
    source,
    message: error?.message || 'Unknown metrics flush error',
  });
};

export const JOURNEY_DEFINITIONS = Object.freeze({
  homeFeed: {
    key: 'homeFeed',
    label: 'Home Feed',
    description: 'The community feed route and its above-the-fold content.',
    type: 'route',
  },
  profile: {
    key: 'profile',
    label: 'Profile',
    description: 'The signed-in account/profile screen.',
    type: 'route',
  },
  postDetail: {
    key: 'postDetail',
    label: 'Post Detail',
    description: 'A single post and its comments/media.',
    type: 'route',
  },
  maintainerDashboard: {
    key: 'maintainerDashboard',
    label: 'Maintainer Dashboard',
    description: 'The maintainer landing page and operational overview.',
    type: 'route',
  },
  maintainerCommunities: {
    key: 'maintainerCommunities',
    label: 'Maintainer Communities',
    description: 'Community setup, editing, and bootstrap workflows.',
    type: 'route',
  },
  maintainerCommunityDetail: {
    key: 'maintainerCommunityDetail',
    label: 'Maintainer Community Detail',
    description: 'Community-level detail and setup completion work.',
    type: 'route',
  },
  maintainerMembers: {
    key: 'maintainerMembers',
    label: 'Maintainer Members',
    description: 'Member directory management and review.',
    type: 'route',
  },
  maintainerBodies: {
    key: 'maintainerBodies',
    label: 'Maintainer Bodies',
    description: 'Body configuration and roster management.',
    type: 'route',
  },
  maintainerImports: {
    key: 'maintainerImports',
    label: 'Maintainer Imports',
    description: 'Bulk member import parsing, validation, and submit flow.',
    type: 'route',
  },
  maintainerRoles: {
    key: 'maintainerRoles',
    label: 'Maintainer Roles',
    description: 'Role review and assignment controls.',
    type: 'route',
  },
  maintainerPermissions: {
    key: 'maintainerPermissions',
    label: 'Maintainer Permissions',
    description: 'Permission mapping and access review tools.',
    type: 'route',
  },
  notifications: {
    key: 'notifications',
    label: 'Notifications',
    description: 'The notifications flyout and first visible state.',
    type: 'overlay',
  },
  messages: {
    key: 'messages',
    label: 'Messages',
    description: 'The chat dock and active conversation loading.',
    type: 'overlay',
  },
  composer: {
    key: 'composer',
    label: 'Composer',
    description: 'The create-post modal and first interactive state.',
    type: 'overlay',
  },
});

export const METRIC_DEFINITIONS = Object.freeze({
  lcp: {
    key: 'lcp',
    label: 'LCP',
    description: 'Largest Contentful Paint.',
    unit: 'ms',
    target: 2500,
    comparator: 'max',
    rollup: 'p75',
  },
  inp: {
    key: 'inp',
    label: 'INP',
    description: 'Interaction to Next Paint.',
    unit: 'ms',
    target: 200,
    comparator: 'max',
    rollup: 'p75',
  },
  cls: {
    key: 'cls',
    label: 'CLS',
    description: 'Cumulative Layout Shift.',
    unit: 'score',
    target: 0.1,
    comparator: 'max',
    rollup: 'p75',
  },
  ttfb: {
    key: 'ttfb',
    label: 'TTFB',
    description: 'Time to first byte.',
    unit: 'ms',
    target: 800,
    comparator: 'max',
    rollup: 'p75',
  },
  fcp: {
    key: 'fcp',
    label: 'FCP',
    description: 'First Contentful Paint.',
    unit: 'ms',
    target: 1800,
    comparator: 'max',
    rollup: 'p75',
  },
  route_transition: {
    key: 'route_transition',
    label: 'Route Transition',
    description: 'Time from route start to usable page content.',
    unit: 'ms',
    target: 1000,
    comparator: 'max',
    rollup: 'p75',
  },
  overlay_open: {
    key: 'overlay_open',
    label: 'Overlay Open',
    description: 'Time from opening an overlay to usable content.',
    unit: 'ms',
    target: 700,
    comparator: 'max',
    rollup: 'p75',
  },
  time_to_first_feed_item: {
    key: 'time_to_first_feed_item',
    label: 'Time to First Feed Item',
    description: 'Time until the first feed card is ready.',
    unit: 'ms',
    target: 1500,
    comparator: 'max',
    rollup: 'p75',
  },
  time_to_first_visible_media: {
    key: 'time_to_first_visible_media',
    label: 'Time to First Visible Media',
    description: 'Time until the first media element is visible/loaded.',
    unit: 'ms',
    target: 2200,
    comparator: 'max',
    rollup: 'p75',
  },
  tap_to_feedback: {
    key: 'tap_to_feedback',
    label: 'Tap to Feedback',
    description: 'Latency from tap to visible optimistic feedback.',
    unit: 'ms',
    target: 100,
    comparator: 'max',
    rollup: 'p75',
  },
  autocomplete_latency: {
    key: 'autocomplete_latency',
    label: 'Autocomplete Latency',
    description: 'Time from input change to suggestion render.',
    unit: 'ms',
    target: 150,
    comparator: 'max',
    rollup: 'p75',
  },
  scroll_fps: {
    key: 'scroll_fps',
    label: 'Scroll FPS',
    description: 'Observed frames per second during rapid scroll.',
    unit: 'fps',
    target: 55,
    comparator: 'min',
    rollup: 'p25',
  },
  scroll_dropped_frame_pct: {
    key: 'scroll_dropped_frame_pct',
    label: 'Dropped Frames',
    description: 'Estimated dropped-frame percentage during scroll.',
    unit: '%',
    target: 5,
    comparator: 'max',
    rollup: 'average',
  },
  dom_nodes: {
    key: 'dom_nodes',
    label: 'DOM Nodes',
    description: 'DOM node count for the current surface.',
    unit: 'count',
    target: 1500,
    comparator: 'max',
    rollup: 'p75',
  },
  react_commit: {
    key: 'react_commit',
    label: 'React Commit',
    description: 'Profiler actualDuration for a render commit.',
    unit: 'ms',
    target: 40,
    comparator: 'max',
    rollup: 'p75',
  },
  long_task: {
    key: 'long_task',
    label: 'Long Task',
    description: 'Main-thread tasks longer than 50ms.',
    unit: 'ms',
    target: 50,
    comparator: 'max',
    rollup: 'p75',
  },
  js_route_kb: {
    key: 'js_route_kb',
    label: 'Route JS',
    description: 'JavaScript loaded during the active route transition.',
    unit: 'kb',
    target: 220,
    comparator: 'max',
    rollup: 'p75',
  },
  boot_time: {
    key: 'boot_time',
    label: 'Boot / Hydration',
    description: 'Time from app start to first route usable state.',
    unit: 'ms',
    target: 2000,
    comparator: 'max',
    rollup: 'p75',
  },
  api_latency: {
    key: 'api_latency',
    label: 'API Latency',
    description: 'Full request duration.',
    unit: 'ms',
    target: 400,
    comparator: 'max',
    rollup: 'p75',
  },
  payload_kb: {
    key: 'payload_kb',
    label: 'Payload Size',
    description: 'Estimated payload size per API response.',
    unit: 'kb',
    target: 200,
    comparator: 'max',
    rollup: 'p75',
  },
  cache_hit_rate: {
    key: 'cache_hit_rate',
    label: 'Cache Hit Rate',
    description: 'Percent of tracked requests served from cache.',
    unit: '%',
    target: 70,
    comparator: 'min',
    rollup: 'average',
  },
  duplicate_request_rate: {
    key: 'duplicate_request_rate',
    label: 'Duplicate Requests',
    description: 'Percent of requests repeated too quickly on the same route.',
    unit: '%',
    target: 5,
    comparator: 'max',
    rollup: 'average',
  },
  community_setup_step_duration: {
    key: 'community_setup_step_duration',
    label: 'Community Step Duration',
    description: 'Time spent on each community setup wizard step before moving on.',
    unit: 'ms',
    target: 15000,
    comparator: 'max',
    rollup: 'p75',
  },
  community_setup_duration: {
    key: 'community_setup_duration',
    label: 'Community Save Duration',
    description: 'Time to create or update a community and finish default body bootstrap.',
    unit: 'ms',
    target: 5000,
    comparator: 'max',
    rollup: 'p75',
  },
  community_setup_success_rate: {
    key: 'community_setup_success_rate',
    label: 'Community Save Success',
    description: 'Percent of community save attempts that complete successfully.',
    unit: '%',
    target: 95,
    comparator: 'min',
    rollup: 'average',
  },
  default_body_setup_success_rate: {
    key: 'default_body_setup_success_rate',
    label: 'Default Body Success',
    description: 'Percent of requested default body bootstrap actions that complete successfully.',
    unit: '%',
    target: 95,
    comparator: 'min',
    rollup: 'average',
  },
  import_parse_duration: {
    key: 'import_parse_duration',
    label: 'Import Parse Duration',
    description: 'Time to parse, dedupe, and validate a CSV before preview.',
    unit: 'ms',
    target: 1200,
    comparator: 'max',
    rollup: 'p75',
  },
  import_duration: {
    key: 'import_duration',
    label: 'Import Duration',
    description: 'Time from import submit to final backend response.',
    unit: 'ms',
    target: 8000,
    comparator: 'max',
    rollup: 'p75',
  },
  import_success_rate: {
    key: 'import_success_rate',
    label: 'Import Success',
    description: 'Percent of import attempts that complete successfully.',
    unit: '%',
    target: 95,
    comparator: 'min',
    rollup: 'average',
  },
  invalid_row_rate: {
    key: 'invalid_row_rate',
    label: 'Invalid Rows',
    description: 'Percent of parsed import rows rejected by validation.',
    unit: '%',
    target: 5,
    comparator: 'max',
    rollup: 'average',
  },
  duplicate_row_rate: {
    key: 'duplicate_row_rate',
    label: 'Duplicate Rows',
    description: 'Percent of parsed import rows skipped as duplicates.',
    unit: '%',
    target: 2,
    comparator: 'max',
    rollup: 'average',
  },
});

const now = () => {
  if (typeof performance !== 'undefined' && typeof performance.now === 'function') {
    return performance.now();
  }
  return Date.now();
};

const nowIso = () => new Date().toISOString();

const round = (value, digits = 2) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return null;
  const factor = 10 ** digits;
  return Math.round(numeric * factor) / factor;
};

const resolveStorage = () => {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

const readState = () => {
  const base = {
    samples: [],
    pending: [],
    lastFlushAt: null,
    lastFlushStatus: 'idle',
  };
  const storage = resolveStorage();
  if (!storage) return base;

  try {
    const raw = storage.getItem(STORAGE_KEY);
    if (!raw) return base;
    const parsed = JSON.parse(raw);
    return {
      ...base,
      ...(parsed && typeof parsed === 'object' ? parsed : {}),
      samples: Array.isArray(parsed?.samples) ? parsed.samples : [],
      pending: Array.isArray(parsed?.pending) ? parsed.pending : [],
    };
  } catch {
    return base;
  }
};

let persistedState = readState();
let lastFlushAt = persistedState.lastFlushAt || null;
let lastFlushStatus = persistedState.lastFlushStatus || 'idle';
let initialized = false;
let flushPromise = null;
let backgroundFlushRequested = false;
// Five metric slots retain navigation identity after ordinary history eviction.
const latestVitalSnapshots = new Map();
let vitalSequence = 0;
const vitalReporterId = `liquid-spirit-web:${globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`}`;
let flushTimerId = null;
let persistTimerId = null;
let persistTimerKind = null;
let bootMetricRecorded = false;
let activeRoute = typeof window !== 'undefined' ? window.location.pathname : '/';
let activeJourney = classifyJourneyFromPath(activeRoute);
let activeRouteStartedAt = now();
const listeners = new Set();
const journeyStarts = new Map();
const interactionStarts = new Map();
const recentRequestFingerprints = new Map();
const routeResourceAccumulator = {
  route: activeRoute,
  journey: activeJourney,
  bytes: 0,
  timerId: null,
};

const resolveRumEndpoint = () =>
  import.meta.env.VITE_MAINTAINER_METRICS_ENDPOINT || DEFAULT_RUM_ENDPOINT;

const resolveSummaryEndpoint = () =>
  import.meta.env.VITE_MAINTAINER_METRICS_SUMMARY_ENDPOINT || DEFAULT_SUMMARY_ENDPOINT;

const pruneSamples = (samples = []) => {
  const cutoff = Date.now() - SAMPLE_RETENTION_MS;
  const next = samples
    .filter((sample) => sample && typeof sample === 'object')
    .filter((sample) => {
      const stamp = Date.parse(sample.timestamp || '');
      return Number.isNaN(stamp) || stamp >= cutoff;
    });

  if (next.length <= MAX_SAMPLES) return next;
  return next.slice(next.length - MAX_SAMPLES);
};

const prunePending = (samples = []) => {
  if (samples.length <= MAX_PENDING) return samples;
  return samples.slice(samples.length - MAX_PENDING);
};

const persistStateNow = () => {
  if (persistTimerId && typeof window !== 'undefined') {
    if (persistTimerKind === 'idle' && typeof window.cancelIdleCallback === 'function') {
      window.cancelIdleCallback(persistTimerId);
    } else {
      window.clearTimeout(persistTimerId);
    }
    persistTimerId = null;
    persistTimerKind = null;
  }

  const storage = resolveStorage();
  if (!storage) return;

  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(persistedState));
  } catch {
    // ignore persistence errors
  }
};

const schedulePersistState = () => {
  if (typeof window === 'undefined') {
    persistStateNow();
    return;
  }
  if (persistTimerId) return;

  const persist = () => {
    persistTimerId = null;
    persistTimerKind = null;
    persistStateNow();
  };

  if (typeof window.requestIdleCallback === 'function') {
    persistTimerId = window.requestIdleCallback(persist, { timeout: 1000 });
    persistTimerKind = 'idle';
    return;
  }

  persistTimerId = window.setTimeout(persist, 1000);
  persistTimerKind = 'timeout';
};

const writeState = (nextState, { persist = 'deferred' } = {}) => {
  persistedState = {
    ...persistedState,
    ...nextState,
    samples: pruneSamples(nextState.samples ?? persistedState.samples ?? []),
    pending: prunePending(nextState.pending ?? persistedState.pending ?? []),
    lastFlushAt,
    lastFlushStatus,
  };

  if (persist === 'immediate') {
    persistStateNow();
  } else {
    schedulePersistState();
  }

  listeners.forEach((listener) => {
    try {
      listener();
    } catch {
      // ignore listener errors
    }
  });

  if (typeof window !== 'undefined') {
    window.dispatchEvent(
      new CustomEvent(UPDATE_EVENT, {
        detail: {
          sampleCount: persistedState.samples.length,
          lastFlushAt,
          lastFlushStatus,
        },
      }),
    );
  }
};

const updateState = (mutate) => {
  const next = mutate({
    ...persistedState,
    samples: [...persistedState.samples],
    pending: [...persistedState.pending],
  });
  writeState(next);
};

const resolveDeviceClass = () => {
  if (typeof window === 'undefined') return 'server';
  const width = Number(window.innerWidth || 0);
  if (width <= 640) return 'mobile';
  if (width <= 1024) return 'tablet';
  return 'desktop';
};

const resolveNetworkType = () => {
  if (typeof navigator === 'undefined') return 'unknown';
  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  return connection?.effectiveType || 'unknown';
};

const resolveRouteContext = (overrides = {}) => ({
  route: overrides.route || activeRoute || '/',
  journey: overrides.journey || activeJourney || 'other',
});

const computeStatus = (metricKey, value) => {
  const definition = METRIC_DEFINITIONS[metricKey];
  if (!definition || !Number.isFinite(Number(value))) return 'unknown';
  const numeric = Number(value);
  const target = Number(definition.target);
  if (!Number.isFinite(target)) return 'unknown';

  if (definition.comparator === 'min') {
    if (numeric >= target) return 'good';
    if (numeric >= target * 0.9) return 'warn';
    return 'bad';
  }

  if (numeric <= target) return 'good';
  if (numeric <= target * 1.25) return 'warn';
  return 'bad';
};

const appendSample = (sample, { queueForRemote = true } = {}) => {
  updateState((state) => {
    const replaceOrAppend = (entries) => entries.some((entry) => entry.id === sample.id)
      ? entries.map((entry) => entry.id === sample.id ? sample : entry)
      : [...entries, sample];
    const samples = replaceOrAppend(state.samples);
    const pending = queueForRemote ? replaceOrAppend(state.pending) : state.pending;
    return {
      ...state,
      samples,
      pending,
    };
  });
};

const flushRouteResourceBytes = () => {
  if (!Number.isFinite(routeResourceAccumulator.bytes) || routeResourceAccumulator.bytes <= 0) {
    routeResourceAccumulator.bytes = 0;
    return;
  }

  const bytes = routeResourceAccumulator.bytes;
  routeResourceAccumulator.bytes = 0;
  recordSample({
    metric: 'js_route_kb',
    value: bytes / 1024,
    route: routeResourceAccumulator.route,
    journey: routeResourceAccumulator.journey,
    metadata: {
      source: 'resource-timing',
    },
  });
};

const scheduleRouteResourceFlush = () => {
  if (typeof window === 'undefined') return;
  if (routeResourceAccumulator.timerId) {
    window.clearTimeout(routeResourceAccumulator.timerId);
  }
  routeResourceAccumulator.timerId = window.setTimeout(() => {
    routeResourceAccumulator.timerId = null;
    flushRouteResourceBytes();
  }, ROUTE_JS_FLUSH_DELAY_MS);
};

export function classifyJourneyFromPath(pathname = '') {
  const path = String(pathname || '');
  if (path.startsWith('/news')) return 'homeFeed';
  if (path.startsWith('/my-account') || path.startsWith('/users/')) return 'profile';
  if (path.startsWith('/posts/') || path.startsWith('/og/posts/')) return 'postDetail';
  if (path === '/maintainer' || path === '/maintainer/') return 'maintainerDashboard';
  if (path.startsWith('/maintainer/communities/')) return 'maintainerCommunityDetail';
  if (path.startsWith('/maintainer/communities')) return 'maintainerCommunities';
  if (path.startsWith('/maintainer/members')) return 'maintainerMembers';
  if (path.startsWith('/maintainer/bodies')) return 'maintainerBodies';
  if (path.startsWith('/maintainer/imports')) return 'maintainerImports';
  if (path.startsWith('/maintainer/roles')) return 'maintainerRoles';
  if (path.startsWith('/maintainer/permissions')) return 'maintainerPermissions';
  return 'other';
}

export const estimatePayloadSizeBytes = (payload) => {
  if (payload === null || payload === undefined) return 0;
  try {
    if (typeof payload === 'string') {
      return new Blob([payload]).size;
    }
    return new Blob([JSON.stringify(payload)]).size;
  } catch {
    return 0;
  }
};

export const subscribeToMaintainerMetrics = (listener) => {
  if (typeof listener !== 'function') return () => {};
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export function recordSample({
  metric,
  value,
  route,
  journey,
  unit,
  rating,
  metadata = {},
}) {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return null;

  const definition = METRIC_DEFINITIONS[metric] || {};
  const resolved = resolveRouteContext({ route, journey });
  const snapshotKey = getWebVitalSnapshotKey({ metric, metadata });
  const latest = latestVitalSnapshots.get(metric);
  const previous = snapshotKey && (getWebVitalSnapshotKey(latest) === snapshotKey ? latest : persistedState.samples.find((sample) => (
    typeof sample?.id === 'string' && sample.id.startsWith(`${vitalReporterId}:`) && getWebVitalSnapshotKey(sample) === snapshotKey
  )));
  const id = previous?.id || (snapshotKey ? `${vitalReporterId}:${++vitalSequence}` : `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
  if (previous && previous.metadata.snapshotRevision >= metadata.snapshotRevision) return previous;
  const sample = {
    id,
    timestamp: nowIso(),
    application: APPLICATION,
    platform: PLATFORM,
    release: import.meta.env.VITE_VERCEL_GIT_COMMIT_SHA || '',
    metric,
    value: round(numericValue, metric === 'cls' ? 4 : 2),
    unit: unit || definition.unit || '',
    route: sanitizeMetricRoute(resolved.route),
    journey: resolved.journey,
    deviceClass: resolveDeviceClass(),
    networkType: resolveNetworkType(),
    status: rating || computeStatus(metric, numericValue),
    metadata: snapshotKey ? { ...metadata } : metadata,
  };

  if (previous) {
    sample.timestamp = previous.timestamp;
    sample.route = previous.route;
    sample.journey = previous.journey;
  }
  if (snapshotKey) latestVitalSnapshots.set(metric, sample);
  appendSample(sample);
  scheduleFlushPendingSamples();
  return sample;
}

export const initializeMaintainerMetrics = () => {
  if (initialized || typeof window === 'undefined') return;
  initialized = true;

  recordSample({
    metric: 'app_session',
    value: 1,
    unit: 'count',
    journey: `${APPLICATION}:session`,
    metadata: { deployment: import.meta.env.MODE || 'unknown' },
  });

  if (activeJourney !== 'other') {
    startJourney(activeJourney, {
      route: activeRoute,
      type: 'route',
    });
  }

  if (typeof PerformanceObserver !== 'undefined') {
    try {
      const longTaskObserver = new PerformanceObserver((list) => {
        list.getEntries().forEach((entry) => {
          recordSample({
            metric: 'long_task',
            value: entry.duration,
            metadata: {
              name: entry.name || 'longtask',
            },
          });
        });
      });
      longTaskObserver.observe({ type: 'longtask', buffered: true });
    } catch {
      // long tasks unsupported
    }

    try {
      const resourceObserver = new PerformanceObserver((list) => {
        list.getEntries().forEach((entry) => {
          const isScript = entry.initiatorType === 'script' || /\.js($|\?)/i.test(entry.name || '');
          if (!isScript) return;
          const bytes = Number(entry.transferSize || entry.encodedBodySize || entry.decodedBodySize || 0);
          if (bytes <= 0) return;
          routeResourceAccumulator.bytes += bytes;
          scheduleRouteResourceFlush();
        });
      });
      resourceObserver.observe({ type: 'resource', buffered: true });
    } catch {
      // resource timing unsupported
    }
  }

  const flushOnBackground = () => {
    persistStateNow();
    flushPendingSamples({ useBeacon: true }).catch((error) => reportMetricsFlushError(error, 'background'));
  };

  window.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      flushOnBackground();
    }
  });
  window.addEventListener('pagehide', flushOnBackground);
  window.addEventListener('online', () => {
    flushPendingSamples().catch((error) => reportMetricsFlushError(error, 'online'));
  });
};

export const setActiveMetricsRoute = (pathname, metadata = {}) => {
  const nextPath = String(pathname || '/');
  if (nextPath === activeRoute) return;

  flushRouteResourceBytes();
  activeRoute = nextPath;
  activeJourney = classifyJourneyFromPath(nextPath);
  activeRouteStartedAt = now();
  routeResourceAccumulator.route = nextPath;
  routeResourceAccumulator.journey = activeJourney;
  routeResourceAccumulator.bytes = 0;

  if (activeJourney !== 'other') {
    startJourney(activeJourney, {
      route: nextPath,
      type: 'route',
      ...metadata,
    });
  }
};

export function startJourney(journey, metadata = {}) {
  if (!journey || journey === 'other') return null;
  const context = resolveRouteContext({ journey, route: metadata.route });
  const entry = {
    startedAt: now(),
    route: context.route,
    journey: context.journey,
    metadata,
    milestones: new Set(),
  };
  journeyStarts.set(journey, entry);
  return entry;
}

export const markJourneyMilestone = (journey, metric, options = {}) => {
  if (!journey || !metric) return null;
  const started = journeyStarts.get(journey);
  const context = resolveRouteContext({
    journey,
    route: options.route || started?.route,
  });
  const entry = started || {
    startedAt: activeRouteStartedAt,
    route: context.route,
    milestones: new Set(),
  };

  const onceKey = options.onceKey || metric;
  if (options.once !== false && entry.milestones.has(onceKey)) {
    return null;
  }

  entry.milestones.add(onceKey);
  journeyStarts.set(journey, entry);

  const duration = options.value ?? (now() - entry.startedAt);
  const sample = recordSample({
    metric,
    value: duration,
    route: context.route,
    journey,
    metadata: options.metadata || {},
  });

  if (!bootMetricRecorded && ROUTE_JOURNEYS.has(journey) && (metric === 'route_transition' || metric === 'time_to_first_feed_item')) {
    bootMetricRecorded = true;
    recordSample({
      metric: 'boot_time',
      value: duration,
      route: context.route,
      journey,
      metadata: {
        sourceMetric: metric,
      },
    });
  }

  return sample;
};

export const startInteractionMetric = (metric = 'tap_to_feedback', metadata = {}) => {
  const id = `${metric}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const context = resolveRouteContext(metadata);
  interactionStarts.set(id, {
    metric,
    startedAt: now(),
    route: context.route,
    journey: context.journey,
    metadata,
  });
  return id;
};

export const endInteractionMetric = (interactionId, options = {}) => {
  const started = interactionStarts.get(interactionId);
  if (!started) return null;
  interactionStarts.delete(interactionId);

  const value = options.value ?? (now() - started.startedAt);
  return recordSample({
    metric: options.metric || started.metric,
    value,
    route: options.route || started.route,
    journey: options.journey || started.journey,
    metadata: {
      ...started.metadata,
      ...(options.metadata || {}),
    },
  });
};

export const cancelInteractionMetric = (interactionId) => {
  if (!interactionId) return;
  interactionStarts.delete(interactionId);
};

export const recordApiRequestMetricStart = ({ path, method = 'GET', route, journey, source = 'api' } = {}) => {
  const context = resolveRouteContext({ route, journey });
  const fingerprint = `${method.toUpperCase()}:${String(path || '')}:${context.route}`;
  const previousSeenAt = recentRequestFingerprints.get(fingerprint);
  const startedAt = now();
  recentRequestFingerprints.set(fingerprint, startedAt);

  return {
    startedAt,
    path: sanitizeMetricRoute(String(path || '')),
    method: method.toUpperCase(),
    route: context.route,
    journey: context.journey,
    source,
    duplicate: Number.isFinite(previousSeenAt) && startedAt - previousSeenAt < DUPLICATE_REQUEST_WINDOW_MS,
  };
};

export const recordApiRequestMetricEnd = (requestContext, {
  status = null,
  payload = null,
  sizeBytes,
  cacheStatus = 'network',
  error = null,
} = {}) => {
  if (!requestContext) return null;

  const duration = now() - requestContext.startedAt;
  const resolvedSizeBytes = Number.isFinite(Number(sizeBytes))
    ? Number(sizeBytes)
    : estimatePayloadSizeBytes(payload);
  const isCacheHit = CACHE_HIT_STATUSES.has(cacheStatus);

  recordSample({
    metric: 'api_latency',
    value: duration,
    route: requestContext.route,
    journey: requestContext.journey,
    metadata: {
      path: requestContext.path,
      method: requestContext.method,
      source: requestContext.source,
      status,
      cacheStatus,
      errored: Boolean(error),
    },
  });

  if (resolvedSizeBytes > 0) {
    recordSample({
      metric: 'payload_kb',
      value: resolvedSizeBytes / 1024,
      route: requestContext.route,
      journey: requestContext.journey,
      metadata: {
        path: requestContext.path,
        method: requestContext.method,
        status,
        cacheStatus,
      },
    });
  }

  recordSample({
    metric: 'cache_hit_rate',
    value: isCacheHit ? 100 : 0,
    route: requestContext.route,
    journey: requestContext.journey,
    metadata: {
      path: requestContext.path,
      method: requestContext.method,
      cacheStatus,
    },
  });

  recordSample({
    metric: 'duplicate_request_rate',
    value: requestContext.duplicate ? 100 : 0,
    route: requestContext.route,
    journey: requestContext.journey,
    metadata: {
      path: requestContext.path,
      method: requestContext.method,
      cacheStatus,
    },
  });
};

export const recordScrollMetrics = ({ journey, fps, droppedFramePct, route, metadata = {} }) => {
  if (Number.isFinite(Number(fps))) {
    recordSample({
      metric: 'scroll_fps',
      value: fps,
      route,
      journey,
      metadata,
    });
  }

  if (Number.isFinite(Number(droppedFramePct))) {
    recordSample({
      metric: 'scroll_dropped_frame_pct',
      value: droppedFramePct,
      route,
      journey,
      metadata,
    });
  }
};

export const recordDomNodeMetric = ({ journey, value, route, metadata = {} }) => {
  recordSample({
    metric: 'dom_nodes',
    value,
    journey,
    route,
    metadata,
  });
};

export const recordReactCommitMetric = ({
  journey,
  actualDuration,
  baseDuration,
  phase,
  id,
  route,
}) => {
  recordSample({
    metric: 'react_commit',
    value: actualDuration,
    journey,
    route,
    metadata: {
      id,
      phase,
      baseDuration: round(baseDuration, 2),
    },
  });
};

export const recordWebVitalMetric = (metric) => {
  if (!metric || !metric.name) return null;
  const normalizedName = String(metric.name || '').trim().toLowerCase();
  if (!METRIC_DEFINITIONS[normalizedName]) return null;

  return recordSample({
    metric: normalizedName,
    value: metric.value,
    route: activeRoute,
    journey: activeJourney,
    metadata: {
      id: metric.id,
      delta: metric.delta,
      rating: metric.rating,
      navigationType: metric.navigationType,
      collector: metric.collector,
      collectorVersion: metric.collectorVersion,
      snapshotRevision: metric.snapshotRevision,
    },
  });
};

const getRollupValue = (sortedValues, rollup = 'p75') => {
  if (!sortedValues.length) return null;
  if (rollup === 'average') {
    const total = sortedValues.reduce((sum, value) => sum + value, 0);
    return total / sortedValues.length;
  }
  if (rollup === 'p25') return quantile(sortedValues, 0.25);
  if (rollup === 'p50') return quantile(sortedValues, 0.5);
  if (rollup === 'p95') return quantile(sortedValues, 0.95);
  return quantile(sortedValues, 0.75);
};

function quantile(sortedValues, q) {
  if (!sortedValues.length) return null;
  if (sortedValues.length === 1) return sortedValues[0];
  const position = (sortedValues.length - 1) * q;
  const base = Math.floor(position);
  const remainder = position - base;
  const lower = sortedValues[base];
  const upper = sortedValues[Math.min(base + 1, sortedValues.length - 1)];
  return lower + (upper - lower) * remainder;
}

const summarizeMetricSamples = (samples, metricKey) => {
  const definition = METRIC_DEFINITIONS[metricKey];
  const numericValues = samples
    .map((sample) => Number(sample.value))
    .filter((value) => Number.isFinite(value))
    .sort((a, b) => a - b);

  if (!numericValues.length) {
    return {
      ...definition,
      count: 0,
      status: 'unknown',
      rollupValue: null,
      p50: null,
      p75: null,
      p95: null,
      average: null,
      latest: null,
    };
  }

  const rollupValue = getRollupValue(numericValues, definition.rollup);
  const latest = samples[samples.length - 1];
  return {
    ...definition,
    count: numericValues.length,
    status: computeStatus(metricKey, rollupValue),
    rollupValue: round(rollupValue, metricKey === 'cls' ? 4 : 2),
    p50: round(quantile(numericValues, 0.5), metricKey === 'cls' ? 4 : 2),
    p75: round(quantile(numericValues, 0.75), metricKey === 'cls' ? 4 : 2),
    p95: round(quantile(numericValues, 0.95), metricKey === 'cls' ? 4 : 2),
    average: round(getRollupValue(numericValues, 'average'), metricKey === 'cls' ? 4 : 2),
    latest: latest ? round(Number(latest.value), metricKey === 'cls' ? 4 : 2) : null,
    updatedAt: latest?.timestamp || null,
  };
};

const summarizeJourneyMetrics = (samples, journeyKey) => {
  const journeySamples = samples.filter((sample) => sample.journey === journeyKey);
  const metrics = {};
  Object.keys(METRIC_DEFINITIONS).forEach((metricKey) => {
    const filtered = journeySamples.filter((sample) => sample.metric === metricKey);
    if (!filtered.length) return;
    metrics[metricKey] = summarizeMetricSamples(filtered, metricKey);
  });

  const deviceBreakdown = ['mobile', 'tablet', 'desktop'].reduce((acc, deviceClass) => {
    const deviceSamples = journeySamples.filter((sample) => sample.deviceClass === deviceClass);
    if (!deviceSamples.length) return acc;
    acc[deviceClass] = {};
    ['lcp', 'inp', 'cls', 'route_transition', 'overlay_open', 'time_to_first_feed_item'].forEach((metricKey) => {
      const filtered = deviceSamples.filter((sample) => sample.metric === metricKey);
      if (!filtered.length) return;
      acc[deviceClass][metricKey] = summarizeMetricSamples(filtered, metricKey);
    });
    return acc;
  }, {});

  return {
    ...JOURNEY_DEFINITIONS[journeyKey],
    sampleCount: journeySamples.length,
    metrics,
    deviceBreakdown,
  };
};

const buildAlerts = (scorecard = []) =>
  scorecard.flatMap((journey) =>
    Object.values(journey.metrics || {})
      .filter((metric) => metric?.status === 'bad')
      .map((metric) => ({
        journey: journey.label,
        metric: metric.label,
        value: metric.rollupValue,
        target: metric.target,
        unit: metric.unit,
        status: metric.status,
      }))
  ).slice(0, 10);

export const getLocalDashboardSnapshot = () => {
  const samples = pruneSamples(persistedState.samples || []);
  const lastSampleAt = samples[samples.length - 1]?.timestamp || null;
  const observedMetrics = [...new Set(samples.map((sample) => sample.metric))].sort();
  const scorecard = Object.keys(JOURNEY_DEFINITIONS).map((journeyKey) =>
    summarizeJourneyMetrics(samples, journeyKey)
  );
  const platformMetrics = {};
  ['long_task', 'js_route_kb', 'boot_time', 'api_latency', 'payload_kb', 'cache_hit_rate', 'duplicate_request_rate'].forEach((metricKey) => {
    const filtered = samples.filter((sample) => sample.metric === metricKey);
    if (!filtered.length) return;
    platformMetrics[metricKey] = summarizeMetricSamples(filtered, metricKey);
  });
  const applicationMetrics = Object.keys(METRIC_DEFINITIONS).reduce((acc, metricKey) => {
    const filtered = samples.filter((sample) => sample.metric === metricKey);
    if (filtered.length) acc[metricKey] = summarizeMetricSamples(filtered, metricKey);
    return acc;
  }, {});
  const expectedMetrics = ['app_session', 'lcp', 'inp', 'cls', 'route_transition', 'api_latency'];
  const application = {
    key: APPLICATION,
    label: 'Liquid Spirit Web',
    platform: PLATFORM,
    category: 'member',
    expectedMetrics,
    observedMetrics,
    missingMetrics: expectedMetrics.filter((metric) => !observedMetrics.includes(metric)),
    sampleCount: samples.length,
    lastSampleAt,
    lastReceivedAt: lastSampleAt,
    status: lastSampleAt ? 'reporting' : 'no-data',
    platforms: [PLATFORM],
    releases: [],
    metrics: applicationMetrics,
  };

  return {
    source: 'local',
    generatedAt: nowIso(),
    sampleCount: samples.length,
    lastSampleAt,
    scorecard,
    platformMetrics,
    applications: [application],
    applicationCoverage: {
      expectedCount: 1,
      reportingCount: application.status === 'reporting' ? 1 : 0,
      staleCount: 0,
      missingCount: application.status === 'no-data' ? 1 : 0,
    },
    alerts: buildAlerts(scorecard),
    pipeline: {
      rumEndpoint: resolveRumEndpoint(),
      summaryEndpoint: resolveSummaryEndpoint(),
      lastFlushAt,
      lastFlushStatus,
      pendingCount: Array.isArray(persistedState.pending) ? persistedState.pending.length : 0,
      speedInsightsEnabled: import.meta.env.VITE_ENABLE_SPEED_INSIGHTS === 'true',
      gtmEnabled: Boolean(import.meta.env.VITE_GTM_ID),
    },
    recentSamples: samples.slice(-20).reverse(),
  };
};

const normalizeRemoteSnapshot = (payload) => {
  if (payload?.scorecard && payload?.pipeline) {
    return {
      ...payload,
      source: payload.source || 'remote',
      pipeline: {
        ...payload.pipeline,
        lastFlushAt: payload.pipeline?.lastFlushAt || lastFlushAt,
        lastFlushStatus: payload.pipeline?.lastFlushStatus || lastFlushStatus,
      },
    };
  }

  if (payload?.data?.scorecard && payload?.data?.pipeline) {
    return normalizeRemoteSnapshot(payload.data);
  }

  return null;
};

export const getMaintainerMetricsSnapshot = async ({ preferRemote = true } = {}) => {
  if (!preferRemote || typeof window === 'undefined') {
    return getLocalDashboardSnapshot();
  }

  try {
    const payload = await apiClient.request(resolveSummaryEndpoint(), {
      method: 'GET',
      contract: 'payload',
      skipMetrics: true,
    });

    const remote = normalizeRemoteSnapshot(payload);
    if (remote) {
      return remote;
    }
  } catch {
    // fall back to local snapshot
  }

  return getLocalDashboardSnapshot();
};

const markFlushState = (status) => {
  lastFlushStatus = status;
  persistedState.lastFlushStatus = status;
  persistedState.lastFlushAt = lastFlushAt;
  writeState(persistedState, { persist: 'immediate' });
};

export async function flushPendingSamples({ useBeacon = false } = {}) {
  if (flushPromise && !useBeacon) return flushPromise;

  const pending = Array.isArray(persistedState.pending) ? [...persistedState.pending] : [];
  if (!pending.length || typeof window === 'undefined') {
    return null;
  }

  const token = resolveStoredAuthToken();
  const headers = {
    'Content-Type': 'application/json',
  };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const body = JSON.stringify({
    schemaVersion: 2,
    sentAt: nowIso(),
    application: APPLICATION,
    platform: PLATFORM,
    release: import.meta.env.VITE_VERCEL_GIT_COMMIT_SHA || '',
    deployment: import.meta.env.MODE || '',
    samples: pending,
  });

  if (useBeacon && typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
    try {
      const blob = new Blob([body], { type: 'application/json' });
      const sent = navigator.sendBeacon(resolveRumEndpoint(), blob);
      if (sent) {
        lastFlushAt = nowIso();
        lastFlushStatus = 'sent';
        updateState((state) => ({
          ...state,
          pending: state.pending.filter((sample) => !pending.some((sent) => sameMetricSnapshot(sample, sent))),
        }));
        persistStateNow();
        return true;
      }
    } catch {
      // fall through to fetch
    }
  }

  if (flushPromise) {
    backgroundFlushRequested = backgroundFlushRequested || useBeacon;
    return flushPromise;
  }

  markFlushState('sending');
  flushPromise = Promise.resolve().then(() => fetch(resolveRumEndpoint(), {
    method: 'POST',
    headers,
    body,
    keepalive: true,
  }))
    .then(async (response) => {
      if (!response.ok) {
        throw new Error(`RUM flush failed with ${response.status}`);
      }
      lastFlushAt = nowIso();
      lastFlushStatus = 'sent';
      updateState((state) => ({
        ...state,
        pending: state.pending.filter((sample) => !pending.some((sent) => sameMetricSnapshot(sample, sent))),
      }));
      return true;
    })
    .catch((error) => {
      lastFlushStatus = error?.message || 'error';
      writeState(persistedState, { persist: 'immediate' });
      return false;
    })
    .finally(() => {
      flushPromise = null;
      if (backgroundFlushRequested) {
        backgroundFlushRequested = false;
        void flushPendingSamples({ useBeacon: true });
      } else if (persistedState.pending.length) {
        scheduleFlushPendingSamples();
      }
    });

  return flushPromise;
}

export function scheduleFlushPendingSamples() {
  if (typeof window === 'undefined') return;
  if (flushTimerId) {
    window.clearTimeout(flushTimerId);
  }
  flushTimerId = window.setTimeout(() => {
    flushTimerId = null;
    flushPendingSamples().catch((error) => reportMetricsFlushError(error, 'timer'));
  }, 15000);
}

const maintainerMetrics = {
  UPDATE_EVENT,
  JOURNEY_DEFINITIONS,
  METRIC_DEFINITIONS,
  initializeMaintainerMetrics,
  classifyJourneyFromPath,
  setActiveMetricsRoute,
  startJourney,
  markJourneyMilestone,
  startInteractionMetric,
  endInteractionMetric,
  cancelInteractionMetric,
  recordApiRequestMetricStart,
  recordApiRequestMetricEnd,
  recordDomNodeMetric,
  recordReactCommitMetric,
  recordScrollMetrics,
  recordWebVitalMetric,
  recordSample,
  estimatePayloadSizeBytes,
  subscribeToMaintainerMetrics,
  getLocalDashboardSnapshot,
  getMaintainerMetricsSnapshot,
  flushPendingSamples,
};

export default maintainerMetrics;
